import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
    Badge,
    Box,
    HStack,
    Link,
    SimpleGrid,
    Text,
    VStack,
} from "@chakra-ui/react";
import CandidateConsentRecords from "./CandidateConsentRecords";
import {
    HiChatBubbleLeftRight,
    HiDocumentText,
    HiOutlineExclamationTriangle,
    HiOutlineQuestionMarkCircle,
    HiOutlineCpuChip,
    HiOutlineShieldCheck,
    HiPhone,
    HiUser,
} from "react-icons/hi2";
import { SectionCard, Grid2 } from "@/components/jobOffer/ui";
import InfoField from "../InfoField";
import AnswerValue from "../AnswerValue";
import { extractConversationFlags } from "./candidateConversationFlags";

const extractInteractionData = (collectedAnswers) => {
    try {
        const answers = typeof collectedAnswers === "string"
            ? JSON.parse(collectedAnswers || "[]")
            : (collectedAnswers ?? []);
        const sorted = [...answers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        const displayAnswers = sorted
            .filter(a => a.display_name && a.value !== "__FORM_ACTION_RESUMED__" && a.value !== "uploaded");

        const qCompleteIdx = sorted.findIndex(a => a.name === "@questionsComplete");
        const dCompleteIdx = sorted.findIndex(a => a.name === "@documentsComplete");
        const qEnd = qCompleteIdx !== -1 ? qCompleteIdx : sorted.length;

        const displayQA = sorted
            .slice(0, qEnd)
            .filter(a => !a.display_name && !a.name.startsWith("@"));

        const displayDocs = qCompleteIdx !== -1 && dCompleteIdx !== -1
            ? sorted.slice(qCompleteIdx + 1, dCompleteIdx).filter(a => !a.display_name && !a.name.startsWith("@"))
            : [];

        return { displayAnswers, displayQA, displayDocs };
    } catch {
        return { displayAnswers: [], displayQA: [], displayDocs: [] };
    }
};

// A plain (non-collapsible) card header -- unlike CollapsibleSection, everything on this
// tab should be visible at once (short, glanceable candidate facts), not hidden behind clicks.
const CardHeader = ({ icon: Icon, title }) => (
    <HStack gap={3} mb={4}>
        <Box p={2} bg="purple.50" borderRadius={8} color="purple.500" display="flex">
            <Icon />
        </Box>
        <Text fontWeight={700} color="gray.900" fontSize="sm">{title}</Text>
    </HStack>
);

const REASON_KEYS = {
    no_knowledge: "reasonNoKnowledge",
    blocked_by_config: "reasonBlockedByConfig",
    confidential: "reasonConfidential",
};

const CATEGORY_KEYS = {
    contact_request: "catContactRequest",
    negotiation: "catNegotiation",
    availability: "catAvailability",
    gdpr_request: "catGdprRequest",
    complaint: "catComplaint",
    other: "catOther",
};

const CandidateAiDataTab = ({ candidate, onOpenDocument, onConsentChanged }) => {
    const { t } = useTranslation("models");
    const { unanswered, humanTopics } = extractConversationFlags(candidate.interactions);
    const fullName = [candidate.firstname, candidate.lastname].filter(Boolean).join(" ") || "-";

    return (
        <VStack gap={4} align="stretch">
            {/* Personal / contact / documents / summary -- laid out side by side so it's all visible at once */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
                <SectionCard>
                    <CardHeader icon={HiUser} title={t("candidateDetailPage.sectionPersonalData")} />
                    <Grid2>
                        <InfoField label={t("candidateDetailPage.fieldFirstname")}>
                            <Text color="gray.900" fontSize="sm">{candidate.firstname ?? "-"}</Text>
                        </InfoField>
                        <InfoField label={t("candidateDetailPage.fieldLastname")}>
                            <Text color="gray.900" fontSize="sm">{candidate.lastname ?? "-"}</Text>
                        </InfoField>
                        <InfoField label={t("candidateDetailPage.fieldFullName")}>
                            <Text color="gray.900" fontSize="sm">{fullName}</Text>
                        </InfoField>
                        <InfoField label={t("candidateDetailPage.fieldLanguage")}>
                            <Text color="gray.900" fontSize="sm">{candidate.language ?? "-"}</Text>
                        </InfoField>
                    </Grid2>
                </SectionCard>

                <SectionCard>
                    <CardHeader icon={HiPhone} title={t("candidateDetailPage.sectionContactData")} />
                    <Grid2>
                        <InfoField label={t("candidateDetailPage.fieldPhone")}>
                            {candidate.phoneNumber
                                ? <Link href={`tel:${candidate.phoneNumber}`} color="gray.900" fontSize="sm">{candidate.phoneNumber}</Link>
                                : <Text color="gray.900" fontSize="sm">-</Text>}
                        </InfoField>
                        <InfoField label={t("candidateDetailPage.fieldEmail")}>
                            {candidate.email
                                ? <Link href={`mailto:${candidate.email}`} color="gray.900" fontSize="sm">{candidate.email}</Link>
                                : <Text color="gray.900" fontSize="sm">-</Text>}
                        </InfoField>
                        <InfoField label={t("candidateDetailPage.fieldLocation")}>
                            <Text color="gray.900" fontSize="sm">{candidate.address?.city ?? "-"}</Text>
                        </InfoField>
                    </Grid2>
                </SectionCard>

                {candidate.documents?.length > 0 && (
                    <SectionCard>
                        <CardHeader icon={HiDocumentText} title={t("candidateDetailPage.sectionDocuments")} />
                        <Grid2>
                            {candidate.documents.map((doc, i) => (
                                <InfoField key={i} label={doc.name}>
                                    {doc.resource?.path
                                        ? (
                                            <Text
                                                color="purple.500"
                                                fontSize="sm"
                                                cursor="pointer"
                                                onClick={() => onOpenDocument({ path: doc.resource.path, name: doc.name })}
                                            >
                                                Podgląd
                                            </Text>
                                        )
                                        : <Text color="gray.900" fontSize="sm">{doc.value ?? "-"}</Text>}
                                </InfoField>
                            ))}
                        </Grid2>
                    </SectionCard>
                )}

                <SectionCard>
                    <CardHeader icon={HiOutlineShieldCheck} title={t("candidateDetailPage.sectionConsents")} />
                    <CandidateConsentRecords candidateId={candidate.id} onChanged={onConsentChanged} />
                </SectionCard>

                {candidate.summary && (
                    <SectionCard>
                        <CardHeader icon={HiOutlineCpuChip} title={t("candidateDetailPage.sectionAiSummary")} />
                        <Text color="gray.900" fontSize="sm" lineHeight={1.7}>{candidate.summary}</Text>
                    </SectionCard>
                )}
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, lg: 2 }} gap={4}>
                <SectionCard>
                    <CardHeader icon={HiOutlineQuestionMarkCircle} title={t("candidateDetailPage.sectionUnanswered")} />
                    {unanswered.length === 0 ? (
                        <Text color="gray.500" fontSize="sm">{t("candidateDetailPage.noUnanswered")}</Text>
                    ) : (
                        <VStack align="stretch" gap={3}>
                            {unanswered.map((q, i) => (
                                <Box key={i} bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius={8} p={3}>
                                    <Text color="gray.900" fontSize="sm" mb={2}>{q.question}</Text>
                                    <HStack gap={2} wrap="wrap">
                                        {q.reason && (
                                            <Badge colorPalette={q.reason === "no_knowledge" ? "gray" : "orange"}>
                                                {REASON_KEYS[q.reason] ? t(`candidateDetailPage.${REASON_KEYS[q.reason]}`) : q.reason}
                                            </Badge>
                                        )}
                                        {q.askedAt && (
                                            <Text color="gray.500" fontSize="xs">{dayjs(q.askedAt).format("DD.MM.YYYY HH:mm")}</Text>
                                        )}
                                    </HStack>
                                </Box>
                            ))}
                        </VStack>
                    )}
                </SectionCard>

                <SectionCard>
                    <CardHeader icon={HiOutlineExclamationTriangle} title={t("candidateDetailPage.sectionHumanTopics")} />
                    {humanTopics.length === 0 ? (
                        <Text color="gray.500" fontSize="sm">{t("candidateDetailPage.noHumanTopics")}</Text>
                    ) : (
                        <VStack align="stretch" gap={3}>
                            {humanTopics.map((h, i) => (
                                <Box
                                    key={i}
                                    bg={h.priority === "high" ? "red.50" : "gray.50"}
                                    border="1px solid"
                                    borderColor={h.priority === "high" ? "red.200" : "gray.200"}
                                    borderRadius={8}
                                    p={3}
                                >
                                    <Text color="gray.900" fontSize="sm" fontWeight={600} mb={2}>{h.topic}</Text>
                                    <HStack gap={2} wrap="wrap" mb={h.evidence ? 2 : 0}>
                                        <Badge colorPalette="purple">
                                            {CATEGORY_KEYS[h.category] ? t(`candidateDetailPage.${CATEGORY_KEYS[h.category]}`) : h.category}
                                        </Badge>
                                        <Badge colorPalette={h.priority === "high" ? "red" : "gray"}>
                                            {t(h.priority === "high" ? "candidateDetailPage.priorityHigh" : "candidateDetailPage.priorityNormal")}
                                        </Badge>
                                    </HStack>
                                    {h.evidence && (
                                        <Text color="gray.600" fontSize="sm" fontStyle="italic">„{h.evidence}”</Text>
                                    )}
                                </Box>
                            ))}
                        </VStack>
                    )}
                </SectionCard>
            </SimpleGrid>

            {/* Interactions */}
            {candidate.interactions?.length > 0 && (
                <SectionCard>
                    <CardHeader icon={HiChatBubbleLeftRight} title={t("candidateDetailPage.sectionInteractions")} />
                    <VStack align="stretch" gap={3}>
                        {candidate.interactions.map((interaction) => {
                            const { displayAnswers, displayQA, displayDocs } = extractInteractionData(interaction.collectedAnswers);
                            const hasAnyData = displayAnswers.length > 0 || displayQA.length > 0 || displayDocs.length > 0;
                            return (
                                <Box
                                    key={interaction.interactionId}
                                    bg="gray.50"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    borderRadius={8}
                                    p={4}
                                >
                                    <HStack justify="space-between" mb={3}>
                                        <Text color="gray.900" fontWeight={600} fontSize="sm">
                                            {interaction.title || t("candidateDetailPage.untitledInteraction")}
                                        </Text>
                                        <Text color="gray.500" fontSize="xs">
                                            {dayjs(interaction.createdAt).format("DD.MM.YYYY HH:mm")}
                                        </Text>
                                    </HStack>

                                    {!hasAnyData && (
                                        <Text color="gray.500" fontSize="sm">{t("candidateDetailPage.noAnswer")}</Text>
                                    )}

                                    {displayAnswers.length > 0 && (
                                        <Box mb={displayQA.length > 0 || displayDocs.length > 0 ? 4 : 0}>
                                            <Text color="gray.500" fontSize="2xs" fontWeight={600} textTransform="uppercase" letterSpacing="wider" mb={2}>
                                                {t("conversationDetailPage.answersTitle")}
                                            </Text>
                                            <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
                                                {displayAnswers.map((a) => (
                                                    <InfoField key={a.name} label={a.display_name}>
                                                        <Text color="gray.900" fontSize="sm">{a.value}</Text>
                                                    </InfoField>
                                                ))}
                                            </SimpleGrid>
                                        </Box>
                                    )}

                                    {displayQA.length > 0 && (
                                        <Box mb={displayDocs.length > 0 ? 4 : 0}>
                                            <Text color="gray.500" fontSize="2xs" fontWeight={600} textTransform="uppercase" letterSpacing="wider" mb={2}>
                                                {t("conversationDetailPage.qaTitle")}
                                            </Text>
                                            <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius={8} overflow="hidden">
                                                {displayQA.map((item, i) => (
                                                    <Box
                                                        key={item.name}
                                                        px={4}
                                                        py={3}
                                                        borderBottom={i < displayQA.length - 1 ? "1px solid" : "none"}
                                                        borderColor="gray.200"
                                                    >
                                                        <Text color="gray.500" fontSize="sm" mb={1}>{item.name}</Text>
                                                        <AnswerValue item={item} />
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    {displayDocs.length > 0 && (
                                        <Box>
                                            <Text color="gray.500" fontSize="2xs" fontWeight={600} textTransform="uppercase" letterSpacing="wider" mb={2}>
                                                {t("conversationDetailPage.docsTitle")}
                                            </Text>
                                            <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius={8} overflow="hidden">
                                                {displayDocs.map((item, i) => (
                                                    <Box
                                                        key={item.name}
                                                        px={4}
                                                        py={3}
                                                        borderBottom={i < displayDocs.length - 1 ? "1px solid" : "none"}
                                                        borderColor="gray.200"
                                                    >
                                                        <Text color="gray.500" fontSize="sm" mb={1}>{item.name}</Text>
                                                        <AnswerValue item={item} />
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </VStack>
                </SectionCard>
            )}
        </VStack>
    );
};

export default CandidateAiDataTab;
